// client/src/components/pages/WeighbridgeCompliance/AuditLog.tsx
// (converted from .js in the WBE batch, ST-00223)
//
// WBE-10: the filters sent labels the data never holds ('Weighbridge',
// 'Notification', 'Created'/'Updated'/'Deleted') while rows are stored as 'Asset',
// 'NotificationConfig', 'CREATE'/'UPDATE'/'DELETE', so any Action filter (and those
// two entity filters) returned nothing, and the date / user filters went out under
// names the server ignored. The dropdowns now send the stored values and show
// labels; badges and the PDF / Excel exports map the stored values to labels.
import ThemedSelect from '../../common/ThemedSelect';
import { Fragment, useState, useEffect, useCallback } from 'react';
import {
  History,
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  FileSpreadsheet,
  Loader2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import type { AxiosResponse } from 'axios';
import { formatAppDateTime, toApiDateString } from '../../../utils/dateUtils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import type { CellHookData, UserOptions } from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import api from '../../../services/weighbridgeComplianceApi';
import type {
  AuditAction,
  AuditEntityType,
  AuditLogParams,
  AuditLogRow,
  PaginatedBody,
} from '../../../types/clientPages/weighbridgeCompliance';

/** Filter value '' = all. Values are what weighbridge_audit_log stores. */
export const ENTITY_TYPE_OPTIONS: Array<{ value: AuditEntityType | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'Asset', label: 'Weighbridge' },
  { value: 'Calibration', label: 'Calibration' },
  { value: 'Verification', label: 'Verification' },
  { value: 'Operator', label: 'Operator' },
  { value: 'OperatorAssignment', label: 'Operator assignment' },
  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'NCR', label: 'NCR' },
  { value: 'Document', label: 'Document' },
  { value: 'NotificationConfig', label: 'Notification settings' },
];

export const ACTION_OPTIONS: Array<{ value: AuditAction | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'CREATE', label: 'Created' },
  { value: 'UPDATE', label: 'Updated' },
  { value: 'DELETE', label: 'Deleted' },
];

/** 'Asset' → 'Weighbridge' etc.; an unknown stored value is shown as stored. */
export const entityTypeLabel = (value: string | null | undefined): string =>
  ENTITY_TYPE_OPTIONS.find((o) => o.value && o.value === value)?.label || value || '';

/** 'CREATE' → 'Created' etc. (older rows written as 'Created' keep their label). */
export const actionLabel = (value: string | null | undefined): string => {
  const upper = String(value || '').toUpperCase();
  const stored = upper.startsWith('CREATE')
    ? 'CREATE'
    : upper.startsWith('UPDATE')
      ? 'UPDATE'
      : upper.startsWith('DELETE')
        ? 'DELETE'
        : '';
  return ACTION_OPTIONS.find((o) => o.value && o.value === stored)?.label || value || '';
};

/** Badge classes and export colours per action label. */
const ACTION_STYLES: Record<
  string,
  { badge: string; dot: string; rgb: [number, number, number]; argb: string }
> = {
  Created: {
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-500',
    rgb: [37, 99, 235],
    argb: 'FF2563EB',
  },
  Updated: {
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    rgb: [217, 119, 6],
    argb: 'FFD97706',
  },
  Deleted: {
    badge: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500',
    rgb: [220, 38, 38],
    argb: 'FFDC2626',
  },
};

/** The jspdf-autotable side-effect import adds doc.autoTable; it is not in jsPDF's types. */
type AutoTableDoc = jsPDF & {
  autoTable: (options: UserOptions) => void;
  internal: jsPDF['internal'] & { getNumberOfPages: () => number };
};

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

function safeFormatDate(dateVal: string | null | undefined): string {
  if (!dateVal) return '—';
  return formatAppDateTime(dateVal) || '—';
}

/** The active filters, as printed on the PDF export. */
export function filterSummary(f: {
  entityType: AuditEntityType | '';
  action: AuditAction | '';
  dateFrom: Date | null;
  dateTo: Date | null;
  performedBy: string;
}): string[] {
  const parts: string[] = [];
  if (f.entityType) parts.push(`Entity: ${entityTypeLabel(f.entityType)}`);
  if (f.action) parts.push(`Action: ${actionLabel(f.action)}`);
  if (f.dateFrom) parts.push(`From: ${toApiDateString(f.dateFrom)}`);
  if (f.dateTo) parts.push(`To: ${toApiDateString(f.dateTo)}`);
  if (f.performedBy) parts.push(`User: ${f.performedBy}`);
  return parts;
}

/** "Field Changed" text: the description, or the number of structured changes. */
const changeSummary = (log: AuditLogRow): string =>
  log.change_description || (log.changes?.length ? `${log.changes.length} field(s)` : '');

const SkeletonRow = () => (
  <tr>
    {Array.from({ length: 9 }).map((_, i) => (
      <td key={i} className="px-5 py-3.5">
        <div
          className="h-4 bg-slate-200 rounded animate-pulse"
          style={{ width: `${40 + Math.random() * 60}%` }}
        />
      </td>
    ))}
  </tr>
);

export const AuditLog = () => {
  const [logs, setLogs] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [entityTypeFilter, setEntityTypeFilter] = useState<AuditEntityType | ''>('');
  const [actionFilter, setActionFilter] = useState<AuditAction | ''>('');
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [performedByFilter, setPerformedByFilter] = useState('');
  const [debouncedPerformedBy, setDebouncedPerformedBy] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPerformedBy(performedByFilter), 300);
    return () => clearTimeout(timer);
  }, [performedByFilter]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: AuditLogParams = {};
      if (entityTypeFilter) params.entityType = entityTypeFilter;
      if (actionFilter) params.action = actionFilter;
      if (dateFrom) params.dateFrom = toApiDateString(dateFrom);
      if (dateTo) params.dateTo = toApiDateString(dateTo);
      if (debouncedPerformedBy) params.performedBy = debouncedPerformedBy;
      const response = (await api.audit.getLog({ ...params })) as AxiosResponse<
        PaginatedBody<AuditLogRow> | AuditLogRow[]
      >;
      const d = response.data;
      setLogs(Array.isArray(d) ? d : d?.data || []);
    } catch (error) {
      // One message per failing endpoint, however often the filters reload it (ST-00259).
      toast.error('Failed to load audit logs', { toastId: 'compliance-audit-load' });
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [entityTypeFilter, actionFilter, dateFrom, dateTo, debouncedPerformedBy]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const toggleRow = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getActionBadge = (action: string | null) => {
    const label = actionLabel(action);
    const style = ACTION_STYLES[label];
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${style?.badge || 'bg-slate-100 text-slate-500 border-slate-200'}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${style?.dot || 'bg-slate-400'}`} />
        {label || '-'}
      </span>
    );
  };

  const handleExportPdf = useCallback(() => {
    if (logs.length === 0) {
      toast.error('No data to export.');
      return;
    }
    setExporting(true);
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' }) as AutoTableDoc;
      const pageWidth = doc.internal.pageSize.getWidth();

      // Header
      doc.setFillColor(69, 183, 170);
      doc.rect(0, 0, pageWidth, 28, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('LexiBridge', 14, 12);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Weighbridge Compliance — Audit Log', 14, 20);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Audit Log Export', pageWidth - 14, 12, { align: 'right' });
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy HH:mm')}`, pageWidth - 14, 20, {
        align: 'right',
      });

      // Filters summary
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(8);
      const filterParts = filterSummary({
        entityType: entityTypeFilter,
        action: actionFilter,
        dateFrom,
        dateTo,
        performedBy: performedByFilter,
      });
      doc.text(
        filterParts.length > 0 ? `Filters: ${filterParts.join(' | ')}` : 'Filters: None',
        14,
        35
      );
      doc.text(`Total records: ${logs.length}`, 14, 40);

      doc.autoTable({
        head: [
          [
            'Timestamp',
            'Entity Type',
            'Entity Name',
            'Action',
            'Field',
            'Old Value',
            'New Value',
            'Changed By',
            'IP',
          ],
        ],
        body: logs.map((log) => [
          safeFormatDate(log.performed_at),
          entityTypeLabel(log.entity_type) || '—',
          // The audit log stores the entity id, not its name.
          log.entity_id ?? '—',
          actionLabel(log.action) || '—',
          changeSummary(log) || '—',
          log.old_values ?? '—',
          log.new_values ?? '—',
          log.performed_by || '—',
          log.ip_address || '—',
        ]),
        startY: 45,
        margin: { left: 14, right: 14 },
        headStyles: {
          fillColor: [69, 183, 170],
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: 'bold',
        },
        bodyStyles: { fontSize: 6.5, textColor: [30, 41, 59] },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          3: { fontStyle: 'bold' },
          5: { cellWidth: 30 },
          6: { cellWidth: 30 },
        },
        didParseCell(data: CellHookData) {
          if (data.column.index === 3 && data.section === 'body') {
            const style = ACTION_STYLES[String(data.cell.raw)];
            if (style) data.cell.styles.textColor = style.rgb;
          }
        },
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFillColor(241, 245, 249);
        doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);
        doc.text('LexiBridge Weighbridge Compliance — Confidential', 14, pageHeight - 4);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, pageHeight - 4, { align: 'right' });
      }

      const timestamp = format(new Date(), 'yyyyMMdd_HHmm');
      triggerDownload(doc.output('blob'), `LexiBridge_AuditLog_${timestamp}.pdf`);
      toast.success('Audit log exported as PDF.');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF.');
    } finally {
      setExporting(false);
    }
  }, [logs, entityTypeFilter, actionFilter, dateFrom, dateTo, performedByFilter]);

  const handleExportExcel = useCallback(async () => {
    if (logs.length === 0) {
      toast.error('No data to export.');
      return;
    }
    setExporting(true);
    try {
      const wb = new ExcelJS.Workbook();
      wb.creator = 'LexiBridge';
      wb.created = new Date();
      const ws = wb.addWorksheet('Audit Log');

      // Title
      ws.mergeCells(1, 1, 1, 9);
      const titleCell = ws.getCell(1, 1);
      titleCell.value = 'LexiBridge Weighbridge Compliance — Audit Log';
      titleCell.font = { bold: true, size: 12, name: 'Calibri', color: { argb: 'FF369D91' } };
      titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
      ws.getRow(1).height = 26;

      ws.mergeCells(2, 1, 2, 9);
      const genCell = ws.getCell(2, 1);
      genCell.value = `Generated: ${format(new Date(), 'dd MMM yyyy HH:mm')}  |  Records: ${logs.length}`;
      genCell.font = { italic: true, size: 8, name: 'Calibri', color: { argb: 'FF64748B' } };
      ws.getRow(2).height = 14;

      // Header row
      const headers = [
        'Timestamp',
        'Entity Type',
        'Entity Name / ID',
        'Action',
        'Field Changed',
        'Old Value',
        'New Value',
        'Changed By',
        'IP Address',
      ];
      const headerRow = ws.getRow(3);
      headers.forEach((h, i) => {
        headerRow.getCell(i + 1).value = h;
      });
      headerRow.height = 22;
      for (let c = 1; c <= 9; c++) {
        const cell = headerRow.getCell(c);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF45B7AA' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10, name: 'Calibri' };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        cell.border = { bottom: { style: 'thin', color: { argb: 'FF369D91' } } };
      }

      // Data
      logs.forEach((log, rowIdx) => {
        const wsRow = ws.getRow(rowIdx + 4);
        const vals: Array<string | number> = [
          safeFormatDate(log.performed_at),
          entityTypeLabel(log.entity_type),
          log.entity_id ?? '',
          actionLabel(log.action),
          changeSummary(log),
          log.old_values ?? '',
          log.new_values ?? '',
          log.performed_by || '',
          log.ip_address || '',
        ];
        vals.forEach((v, colIdx) => {
          const cell = wsRow.getCell(colIdx + 1);
          cell.value = v;
          cell.font = { size: 9, name: 'Calibri' };
          cell.alignment = { vertical: 'middle' };
        });
        wsRow.height = 16;

        // Action column coloring
        const actionCell = wsRow.getCell(4);
        const actionStyle = ACTION_STYLES[actionLabel(log.action)];
        if (actionStyle) {
          actionCell.font = {
            bold: true,
            color: { argb: actionStyle.argb },
            size: 9,
            name: 'Calibri',
          };
        }

        // Alternating rows
        if (rowIdx % 2 === 1) {
          for (let c = 1; c <= 9; c++) {
            wsRow.getCell(c).fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF8FFFE' },
            };
          }
        }
      });

      // Auto-fit columns
      ws.columns.forEach((col) => {
        let maxLen = 10;
        col.eachCell?.({ includeEmpty: false }, (cell) => {
          const len = cell.value ? String(cell.value).length : 0;
          if (len > maxLen) maxLen = len;
        });
        col.width = Math.min(maxLen + 4, 50);
      });

      const timestamp = format(new Date(), 'yyyyMMdd_HHmm');
      const buffer = await wb.xlsx.writeBuffer();
      triggerDownload(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `LexiBridge_AuditLog_${timestamp}.xlsx`
      );
      toast.success('Audit log exported as Excel.');
    } catch (err) {
      console.error('Excel export error:', err);
      toast.error('Failed to export Excel.');
    } finally {
      setExporting(false);
    }
  }, [logs]);

  const renderChangeDetails = (log: AuditLogRow) => {
    // The server sends change_description + old_values/new_values; a structured
    // `changes` array is rendered when present.
    const changes = log.changes || [];
    if (changes.length === 0 && !log.change_description) {
      return <p className="text-sm text-slate-400 italic">No detail available</p>;
    }

    if (changes.length > 0) {
      return (
        <div className="space-y-2">
          {changes.map((change, idx) => (
            <div
              key={idx}
              className="flex items-start gap-4 bg-surface rounded-lg border border-slate-100 px-4 py-2.5"
            >
              <div className="min-w-[120px]">
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  {change.field || '-'}
                </span>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
                    Old Value
                  </p>
                  <p className="text-sm text-slate-600 break-words">{change.oldValue ?? '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
                    New Value
                  </p>
                  <p className="text-sm text-slate-700 font-medium break-words">
                    {change.newValue ?? '-'}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="flex items-start gap-4 bg-surface rounded-lg border border-slate-100 px-4 py-2.5">
        <div className="min-w-[120px]">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
            {log.change_description || '-'}
          </span>
        </div>
        <div className="flex-1 grid grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
              Old Value
            </p>
            <p className="text-sm text-slate-600 break-words">{log.old_values ?? '-'}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
              New Value
            </p>
            <p className="text-sm text-slate-700 font-medium break-words">
              {log.new_values ?? '-'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Audit Log
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Track all changes and actions across the compliance module
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPdf}
            disabled={exporting || logs.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-surface text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FileText className="w-3.5 h-3.5" />
            )}
            PDF
          </button>
          <button
            onClick={handleExportExcel}
            disabled={exporting || logs.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-surface text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-3.5 h-3.5" />
            )}
            Excel
          </button>
        </div>
      </div>

      <div className="flex items-end gap-3 mb-4 flex-wrap">
        <div className="relative min-w-[150px]">
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
            Entity Type
          </label>
          <ThemedSelect
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(String(e.target.value) as AuditEntityType | '')}
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            {ENTITY_TYPE_OPTIONS.map((t) => (
              <option key={t.value || 'all'} value={t.value}>
                {t.label}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div className="relative">
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
            Action
          </label>
          <ThemedSelect
            value={actionFilter}
            onChange={(e) => setActionFilter(String(e.target.value) as AuditAction | '')}
            className="pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm appearance-none bg-surface"
          >
            {ACTION_OPTIONS.map((t) => (
              <option key={t.value || 'all'} value={t.value}>
                {t.label}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
            Date From
          </label>
          <DatePicker
            selected={dateFrom}
            onChange={(date: Date | null) => setDateFrom(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="Start date"
            isClearable
            className="w-36 px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
            Date To
          </label>
          <DatePicker
            selected={dateTo}
            onChange={(date: Date | null) => setDateTo(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="End date"
            isClearable
            className="w-36 px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
            Performed By
          </label>
          <input
            type="text"
            value={performedByFilter}
            onChange={(e) => setPerformedByFilter(e.target.value)}
            placeholder="Filter by user..."
            className="w-44 px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
          />
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-100">
                <th className="px-3 py-3 w-8"></th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Timestamp
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Entity Type
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Entity Name / ID
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Action
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Field Changed
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Old Value
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  New Value
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Changed By
                </th>
                <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  IP Address
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
                        <History className="w-8 h-8 text-slate-300" />
                      </div>
                      <h3 className="text-base font-semibold text-slate-700 mb-1">
                        No audit logs found
                      </h3>
                      <p className="text-sm text-slate-400 max-w-sm">
                        {entityTypeFilter || actionFilter || dateFrom || dateTo || performedByFilter
                          ? 'Try adjusting your filter criteria'
                          : 'Audit logs will appear here as changes are made in the system'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <Fragment key={log.id}>
                    <tr
                      className="hover:bg-primary-50/30 cursor-pointer transition-colors"
                      onClick={() => toggleRow(log.id)}
                    >
                      <td className="px-3 py-3.5 text-center">
                        <button className="p-0.5 rounded text-slate-400 hover:text-slate-600 transition-colors">
                          {expandedRows.has(log.id) ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          {log.performed_at ? formatAppDateTime(log.performed_at) : '-'}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-600">
                          {entityTypeLabel(log.entity_type) || '-'}
                        </span>
                      </td>
                      {/* The audit log stores the entity id, not its name. */}
                      <td
                        className="px-5 py-3.5 text-sm text-slate-700 font-medium max-w-[180px] truncate"
                        title={log.entity_id != null ? String(log.entity_id) : ''}
                      >
                        {log.entity_id ?? '-'}
                      </td>
                      <td className="px-5 py-3.5">{getActionBadge(log.action)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">
                        {changeSummary(log) || '-'}
                      </td>
                      <td
                        className="px-5 py-3.5 text-sm text-slate-500 max-w-[140px] truncate"
                        title={log.old_values || ''}
                      >
                        {log.old_values ?? '-'}
                      </td>
                      <td
                        className="px-5 py-3.5 text-sm text-slate-700 max-w-[140px] truncate"
                        title={log.new_values || ''}
                      >
                        {log.new_values ?? '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">
                        {log.performed_by || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-500 font-mono text-xs">
                        {log.ip_address || '-'}
                      </td>
                    </tr>
                    {expandedRows.has(log.id) && (
                      <tr>
                        <td colSpan={10} className="bg-slate-50/60 px-8 py-4">
                          <div className="space-y-2">
                            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                              Change Details
                            </h4>
                            {renderChangeDetails(log)}
                            <div className="flex items-center gap-6 pt-2 text-xs text-slate-400">
                              {log.performed_by && (
                                <span>
                                  User:{' '}
                                  <span className="text-slate-600 font-medium">
                                    {log.performed_by}
                                  </span>
                                </span>
                              )}
                              {log.ip_address && (
                                <span>
                                  IP:{' '}
                                  <span className="text-slate-600 font-mono">{log.ip_address}</span>
                                </span>
                              )}
                              {log.performed_at && (
                                <span>
                                  Time:{' '}
                                  <span className="text-slate-600">
                                    {formatAppDateTime(log.performed_at)}
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
